interface SearchResult {
  startIndex: number
  endIndex: number
}

// 본문이 shadow DOM 에 mount 되면(bodyIsolation 'shadow-dom') textContent·TreeWalker 가 shadow 안을 보지 못한다.
// shadow 본문이 없으면 기존 경로(textContent + lazy TreeWalker)를 그대로 쓴다 — 대형 본문 검색 비용을 늘리지 않는다.
function hasShadowBody(root: Node): boolean {
  if (root instanceof Element && root.shadowRoot != null) return true
  if (!(root instanceof Element || root instanceof DocumentFragment)) return false
  return Array.from(root.querySelectorAll('.htmlMailBody')).some((host) => host.shadowRoot != null)
}

// root 아래 text node 를 composed tree 순서로 모은다 — shadow host 를 만나면 그 shadowRoot 안을 먼저 돈다.
// shadow 가 없으면 TreeWalker(SHOW_TEXT) 결과와 같고, 이어 붙인 문자열은 root.textContent 와 같다.
export function collectComposedTextNodes(root: Node): Text[] {
  const texts: Text[] = []
  const visit = (node: Node) => {
    if (node instanceof Element && node.shadowRoot != null) visit(node.shadowRoot)
    const walker = document.createTreeWalker(node, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT)
    for (let cur = walker.nextNode(); cur != null; cur = walker.nextNode()) {
      if (cur.nodeType === Node.TEXT_NODE) texts.push(cur as Text)
      else if ((cur as Element).shadowRoot != null) visit((cur as Element).shadowRoot!)
    }
  }
  // Document 의 textContent 는 null 이라 기존에도 검색 결과가 없었다.
  if (root.nodeType !== Node.DOCUMENT_NODE) visit(root)
  return texts
}

/**
 * 재등록 판정 key — 본문 교체 여부만 보면 되므로 순서가 아니라 내용이 같으면 된다. JS 순회(collectComposedTextNodes) 없이
 * native textContent(root + 각 본문 shadowRoot)만 이어 붙인다. 대형 본문에서 매 commit 마다 돈다.
 */
export function composedTextKey(root: Node): string | null {
  if (!hasShadowBody(root)) return root.textContent
  const el = root as Element | DocumentFragment
  const hosts = Array.from(el.querySelectorAll('.htmlMailBody'))
  if (root instanceof Element) hosts.unshift(root)
  return root.textContent + hosts.map((host) => host.shadowRoot?.textContent ?? '').join('')
}

export function findTextRanges(root: Node, searchTexts: string | string[]): Range[] {
  const searchTerms: string[] = Array.isArray(searchTexts) ? searchTexts : [searchTexts]
  const searchTermsSet = new Set(searchTerms)
  const textNodes = hasShadowBody(root) ? collectComposedTextNodes(root) : null

  // 1. 모든 검색어의 위치 찾기
  const text =
    (textNodes != null
      ? textNodes.map((node) => node.data).join('')
      : root.textContent
    )?.toLocaleLowerCase() ?? ''
  const results: SearchResult[] = []

  searchTermsSet.forEach((searchText) => {
    if (!searchText) return

    let startIndex = 0
    while (true) {
      const index = text.indexOf(searchText.toLocaleLowerCase(), startIndex)
      if (index === -1) break

      results.push({
        startIndex: index,
        endIndex: index + searchText.length,
      })
      startIndex = index + searchText.length
    }
  })

  // 2. 시작 위치 기준으로 정렬
  results.sort((a, b) => a.startIndex - b.startIndex)

  // 3. 각 결과에 대한 DOM Range 생성
  const walker = textNodes == null ? document.createTreeWalker(root, NodeFilter.SHOW_TEXT) : null
  let nodeIndex = 0
  const nextTextNode = (): Node | null =>
    walker != null ? walker.nextNode() : (textNodes![nodeIndex++] ?? null)
  let currentNode: Node | null = null
  let currentIndex = 0

  return results
    .map((result) => {
      const range = document.createRange()

      while (currentIndex < result.endIndex) {
        currentNode ??= nextTextNode()
        if (!currentNode) return null

        const nodeEndIndex = currentIndex + currentNode.textContent.length

        if (currentIndex <= result.startIndex && result.startIndex < nodeEndIndex) {
          range.setStart(currentNode, result.startIndex - currentIndex)
        }

        if (currentIndex <= result.endIndex && result.endIndex <= nodeEndIndex) {
          range.setEnd(currentNode, result.endIndex - currentIndex)
          return range
        }

        currentIndex = nodeEndIndex
        currentNode = null
      }
    })
    .filter(Boolean)
    .filter((range) => !spansMultipleLines(range))
}

const spansMultipleLines = (range: Range): boolean => {
  const commonAncestor = range.commonAncestorContainer
  const { startContainer, endContainer } = range

  // 줄바꿈을 발생시키는 display 값들
  const lineBreakDisplays = new Set([
    'block',
    'flex',
    'grid',
    'table',
    'flow-root',
    'list-item',
    'table-row',
    'table-cell',
    'table-caption',
  ])

  // 노드의 computed style을 확인하여 줄바꿈을 일으키는지 확인
  const causesLineBreak = (node: Node): boolean => {
    if (node.nodeType !== Node.ELEMENT_NODE) return false

    const element = node as Element
    const computedStyle = window.getComputedStyle(element)

    // display 속성 체크
    if (lineBreakDisplays.has(computedStyle.display)) return true

    // float 속성 체크 (left/right는 레이아웃 변경 가능)
    if (computedStyle.float !== 'none') return true

    // position 속성 체크 (absolute/fixed는 일반 흐름에서 제외)
    if (computedStyle.position === 'absolute' || computedStyle.position === 'fixed') return true

    return false
  }

  // 공통 조상까지 모든 부모 노드 가져오기
  const getParentsUpToAncestor = (node: Node, ancestor: Node): Node[] => {
    const parents: Node[] = []
    let current: Node | null = node

    while (current && current !== ancestor) {
      parents.push(current)
      current = current.parentNode
    }

    return parents
  }

  // start와 end 컨테이너 사이의 모든 요소 찾기
  const startParents = getParentsUpToAncestor(startContainer, commonAncestor)
  const endParents = getParentsUpToAncestor(endContainer, commonAncestor)

  // 시작과 끝의 경로에 줄바꿈 요소가 있는지 확인
  const hasLineBreakInStartPath = startParents.some(causesLineBreak)
  const hasLineBreakInEndPath = endParents.some(causesLineBreak)

  return hasLineBreakInStartPath || hasLineBreakInEndPath
}
